// Rules -> Grouping (owner, 2026-10-22 — D-402). One row per rule, as on
// Codes, each read back as a full sentence:
//
//   When a line item from GORDON FOOD SERVICE is "chicken breast 10 lb",
//   group it under Poultry.
//   When a line item from GORDON FOOD SERVICE is relevant, offer the group
//   Poultry.
//
// A group is a name scoped to one vendor, or to Any vendor, and is never shared
// across vendors: the editor's group picker offers the groups established for
// the vendor chosen (and Any vendor's), and a typed name not among them
// defines a new one. A specific rule names the one description it matches; a
// relevant one names none, and the matching agent judges which lines belong.
//
// THE CODES TAB'S CONVENTIONS, unchanged: the Source filter (Rules or Memory)
// starts on Rules (D-367); editing a Memory row makes it a Rule and a Memory row
// a person turns off stays off — both the server's (`routes/grouping.ts`); the
// row is the edit control; Status is a dot and a word, shown in the editor on
// an edit only; every placeholder is an example.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components'
import { AddRegular, DismissRegular } from '@fluentui/react-icons'
import { GROUPING_MATCH_KIND, GROUPING_RULE_SOURCE, type GroupingMatchKind } from '@ap/shared'
import type { GroupingRule, GroupingRulePatchRequest } from '../../api/types'
import { AppDialog } from '../../components/AppDialog'
import { useDialogFormStyles } from '../../components/dialogForm'
import { EntityPicker, type EntityOption } from '../../components/EntityPicker'
import { FieldInput } from '../../components/FieldControls'
import { FrozenWhileClosed } from '../../components/FrozenWhileClosed'
import { ListFilters } from '../../components/ListFilters'
import { RuleTable } from '../../components/RuleTable'
import { ScreenActions } from '../../components/ScreenActions'
import { ErrorBanner } from '../../components/States'
import { StatusToggle } from '../../components/StatusToggle'
import { useToast } from '../../components/useToast'
import { popupPositionProps, usePopupListboxClass } from '../../components/popupPositioning'
import { errorMessage, writeError } from '../../api/errors'
import { createGroupingRule, loadGroupingRules, loadLineGroups, searchVendors, updateGroupingRule } from '../../lib/data'
import { NOTHING_CHANGED, savedToast, vendorOptionSubtext } from '../../lib/format'
import { useDialogError } from '../../lib/hooks'
import { newGroupLabel, typedGroupName } from '../../lib/groupNameOptions'
import { groupingRuleSentence, groupingRuleVendor as vendorOf } from '../../lib/groupingRuleSentence'
import { DEFAULT_RULE_SOURCE_FILTER, RULE_SOURCE_OPTIONS, matchesRuleSource } from '../../lib/ruleSource'
import { DEFAULT_STATUS_FILTER, matchesStatus, type StatusFilter } from '../../lib/statusFilter'

const useStyles = makeStyles({
  pickerRow: { display: 'flex', gap: '8px', alignItems: 'flex-end' },
  grow: { flexGrow: 1, minWidth: 0 },
})

const ANY_VENDOR_ID = '__any_vendor__'
const ANY_VENDOR_LABEL = 'Any vendor'
/** A typed group name not among the vendor's groups: the option that defines it. */
const NEW_GROUP_PREFIX = '__new_group__:'

const rowId = (row: GroupingRule) => row.id
const rowIsActive = (row: GroupingRule) => row.is_active
const isMemoryRule = (r: GroupingRule) => r.source === GROUPING_RULE_SOURCE.Learned
const writeActive = (r: GroupingRule, active: boolean) =>
  updateGroupingRule(r.id, { is_active: active, row_version: r.row_version })

export function GroupingRulesManager() {
  const toast = useToast()
  const [all, setAll] = useState<GroupingRule[] | null>(null)
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS_FILTER)
  const [source, setSource] = useState<string[]>(DEFAULT_RULE_SOURCE_FILTER)
  const [error, setError] = useState<string | null>(null)
  const [edit, setEdit] = useState<GroupingRule | 'new' | null>(null)
  const [term, setTerm] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      setAll(await loadGroupingRules())
    } catch (e) {
      setError(errorMessage(e))
      setAll([])
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase()
    // The structured fields, not the sentence: its template words would match every row.
    const haystack = (r: GroupingRule) => [r.vendor_id ?? '', vendorOf(r), r.description ?? '', r.group_name].join(' ').toLowerCase()
    return (all ?? [])
      .filter((r) => matchesStatus(status, r.is_active))
      .filter((r) => matchesRuleSource(source, isMemoryRule(r)))
      .filter((r) => !q || haystack(r).includes(q))
  }, [all, status, source, term])

  if (all === null) return <Spinner label="Loading rules…" />

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <ScreenActions>
        <Tooltip content="Add Rule" relationship="label">
          <Button appearance="secondary" icon={<AddRegular />} onClick={() => setEdit('new')} />
        </Tooltip>
        <ListFilters
          status={status}
          onStatusChange={setStatus}
          term={term}
          onTermChange={setTerm}
          searchPlaceholder="Search vendor, description, or group…"
          ariaLabel="Grouping Rules"
          selects={[
            {
              multiple: true as const,
              label: 'Source',
              values: source,
              onChange: setSource,
              options: RULE_SOURCE_OPTIONS,
              defaultValues: DEFAULT_RULE_SOURCE_FILTER,
            },
          ]}
        />
      </ScreenActions>
      <RuleTable
        rows={visible}
        emptyText={(all ?? []).length === 0 ? 'No rules to show.' : 'No rules match.'}
        ariaLabel="Grouping rules"
        selectAllLabel="grouping rules"
        keyOf={rowId}
        isActive={rowIsActive}
        sentence={groupingRuleSentence}
        write={writeActive}
        noun="rule"
        onEdit={setEdit}
        onMarked={(m: string) => {
          toast(m)
          void load()
        }}
        onError={setError}
      />
      <GroupingRuleDialog
        row={edit}
        onClose={() => setEdit(null)}
        onSaved={(message) => {
          setEdit(null)
          toast(message)
          void load()
        }}
      />
    </div>
  )
}

interface GroupChoice {
  /** The established group's id; absent for a new name. */
  id?: string
  name: string
}

function GroupingRuleDialog({
  row,
  onClose,
  onSaved,
}: {
  row: GroupingRule | 'new' | null
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const s = useStyles()
  const df = useDialogFormStyles()
  const [saveError, setSaveError] = useDialogError(row)
  const isNew = row === 'new'
  const existing = row && row !== 'new' ? row : null
  const [dialogSurface, setDialogSurface] = useState<HTMLElement | null>(null)
  const listboxClass = usePopupListboxClass()
  const [vendor, setVendor] = useState<{ id?: string; name?: string }>({})
  const [anyVendor, setAnyVendor] = useState(false)
  /** "specific text" or "relevant": a new rule starts unanswered, as on Codes. */
  const [kind, setKind] = useState<GroupingMatchKind | null>(null)
  const [description, setDescription] = useState('')
  const [group, setGroup] = useState<GroupChoice | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    if (row === 'new') {
      setVendor({})
      setAnyVendor(false)
      setKind(null)
      setDescription('')
      setGroup(null)
      setIsActive(true)
      return
    }
    setVendor(row.vendor_id === null ? {} : { id: row.vendor_id, name: row.vendor_name ?? row.vendor_id })
    setAnyVendor(row.vendor_id === null)
    setKind(row.match_kind)
    setDescription(row.description ?? '')
    setGroup({ id: row.group_id, name: row.group_name })
    setIsActive(row.is_active)
  }, [row])

  const vendorId = anyVendor ? null : (vendor.id ?? null)
  const vendorAnswered = anyVendor || !!vendor.id
  const specific = kind === GROUPING_MATCH_KIND.specific
  const ready = vendorAnswered && kind !== null && (!specific || description.trim() !== '') && group !== null && group.name.trim() !== ''

  /** The vendor's groups, then Any vendor's; a typed name not among them is offered as new. */
  const searchGroups = useCallback(
    async (t: string): Promise<EntityOption[]> => {
      const groups = await loadLineGroups(vendorId)
      const q = typedGroupName(t)
      const options = groups
        .filter((g) => q === '' || g.name.toLowerCase().includes(q.toLowerCase()))
        .map((g) => ({ id: g.id, primary: g.name, ...(g.vendor_id === null ? { secondary: ANY_VENDOR_LABEL } : {}) }))
      // ALWAYS FIRST (D-411). A typed name that is already a group picks that group.
      const exact = groups.find((g) => g.name.toLowerCase() === q.toLowerCase())
      return [{ id: exact ? exact.id : `${NEW_GROUP_PREFIX}${q}`, primary: newGroupLabel(q), boxText: exact ? exact.name : q }, ...options]
    },
    [vendorId],
  )

  async function save() {
    if (!ready || group === null || kind === null) return
    setSaving(true)
    try {
      const groupRef = group.id !== undefined ? { group_id: group.id } : { group_name: group.name.trim() }
      const text = specific ? description.trim() : null
      if (isNew) {
        await createGroupingRule({ vendor_id: vendorId, match_kind: kind, description: text, ...groupRef })
      } else if (existing) {
        const changes: GroupingRulePatchRequest = { row_version: existing.row_version }
        if (vendorId !== existing.vendor_id) changes.vendor_id = vendorId
        if (kind !== existing.match_kind) changes.match_kind = kind
        if ((text ?? '') !== (existing.description ?? '')) changes.description = text
        if (group.id !== existing.group_id) Object.assign(changes, groupRef)
        if (isActive !== existing.is_active) changes.is_active = isActive
        if (Object.keys(changes).length === 1) {
          onSaved(NOTHING_CHANGED)
          return
        }
        await updateGroupingRule(existing.id, changes)
      }
      onSaved(savedToast('rule', { wasNew: isNew, activeBefore: existing?.is_active, activeAfter: isActive }))
    } catch (e) {
      setSaveError(writeError(e, 'Could not save rule'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog open={!!row} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setDialogSurface}>
        <FrozenWhileClosed open={!!row}>
          <DialogBody>
            <DialogTitle>{isNew ? 'Add Rule' : 'Edit Rule'}</DialogTitle>
            <DialogContent className={mergeClasses(df.stack, df.beforeActions)}>
              <ErrorBanner message={saveError} />
              <Field label="When a line item from">
                <div className={s.pickerRow}>
                  <div className={s.grow}>
                    <EntityPicker
                      value={anyVendor ? { id: ANY_VENDOR_ID, name: ANY_VENDOR_LABEL } : vendor}
                      placeholder="e.g. GORDON FOOD SERVICE"
                      search={(t) =>
                        searchVendors(t).then<EntityOption[]>((vs) => [
                          ...(t.trim() === '' || ANY_VENDOR_LABEL.toLowerCase().includes(t.trim().toLowerCase())
                            ? [{ id: ANY_VENDOR_ID, primary: ANY_VENDOR_LABEL }]
                            : []),
                          ...vs.map((v) => ({
                            id: v.vendor_id,
                            primary: v.vendor_name || '(unnamed)',
                            secondary: vendorOptionSubtext(v) || undefined,
                          })),
                        ])
                      }
                      onSelect={(o) => {
                        // Groups belong to one vendor: a new vendor clears the group.
                        setGroup(null)
                        if (o.id === ANY_VENDOR_ID) {
                          setAnyVendor(true)
                          setVendor({})
                          return
                        }
                        setAnyVendor(false)
                        setVendor({ id: o.id, name: o.primary })
                      }}
                    />
                  </div>
                  {(anyVendor || vendor.id) && (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DismissRegular />}
                      aria-label="Clear Vendor"
                      onClick={() => {
                        setAnyVendor(false)
                        setVendor({})
                        setGroup(null)
                      }}
                    />
                  )}
                </div>
              </Field>
              <Field label="is">
                <Dropdown
                  placeholder="e.g. specific text"
                  value={kind === null ? '' : specific ? 'specific text' : 'relevant'}
                  selectedOptions={kind === null ? [] : [kind]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue === undefined) return
                    setKind(d.optionValue as GroupingMatchKind)
                  }}
                  listbox={{ className: listboxClass }}
                  {...popupPositionProps(dialogSurface)}
                >
                  <Option value={GROUPING_MATCH_KIND.specific} text="specific text">
                    specific text
                  </Option>
                  <Option value={GROUPING_MATCH_KIND.relevant} text="relevant">
                    relevant
                  </Option>
                </Dropdown>
              </Field>
              {specific && (
                <Field label="that reads">
                  <FieldInput value={description} onChange={(_, d) => setDescription(d.value)} placeholder="e.g. chicken breast 10 lb" />
                </Field>
              )}
              <Field label={specific || kind === null ? 'group it under' : 'offer the group'}>
                <EntityPicker
                  value={group === null ? {} : { id: group.id ?? `${NEW_GROUP_PREFIX}${group.name}`, name: group.name }}
                  placeholder="e.g. Poultry"
                  optionsKey={vendorId ?? ANY_VENDOR_ID}
                  disabled={!vendorAnswered}
                  search={searchGroups}
                  onSelect={(o) =>
                    o.id === NEW_GROUP_PREFIX
                      ? undefined // nothing typed yet: no name to make a group of
                      : setGroup(o.id.startsWith(NEW_GROUP_PREFIX) ? { name: o.id.slice(NEW_GROUP_PREFIX.length) } : { id: o.id, name: o.boxText ?? o.primary })
                  }
                />
              </Field>
            </DialogContent>
            {!isNew && (
              <DialogActions position="start">
                <StatusToggle value={isActive} onChange={setIsActive} />
              </DialogActions>
            )}
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" disabled={!ready || saving} onClick={() => void save()}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
